import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Add, Minus, SearchNormal1 } from "iconsax-react";
import { Avatar, ModalWrapper, Button, Input, Prompt, ScrollArea } from "@/shared/ui";
import { extractErrorMessage } from "@/shared/utils";
import {
  useGetProjectMembersQuery,
  useGetOrgMembersQuery,
  useAddProjectMemberMutation,
  useRemoveProjectMemberMutation,
} from "../api/membersApi";

interface Props {
  projectId: number;
  onClose: () => void;
}

export default function MembersModal({ projectId, onClose }: Props) {
  const { t } = useTranslation("Common");
  const [search, setSearch] = useState("");
  const [addingId, setAddingId] = useState<number | null>(null);
  const [removingId, setRemovingId] = useState<number | null>(null);
  const [prompt, setPrompt] = useState<{ title: string; text: string; variant: "success" | "error" } | null>(null);

  const { data: members = [] } = useGetProjectMembersQuery(projectId);
  const { data: orgMembers = [] } = useGetOrgMembersQuery();
  const [addMember] = useAddProjectMemberMutation();
  const [removeMember] = useRemoveProjectMemberMutation();

  const isAnyLoading = addingId !== null || removingId !== null;

  const memberUserIds = useMemo(() => new Set(members.map((m) => m.id)), [members]);

  const availableMembers = useMemo(() => {
    const filtered = orgMembers.filter((m) => !memberUserIds.has(m.id));
    if (!search.trim()) return filtered;
    const q = search.trim().toLowerCase();
    return filtered.filter((m) => m.name.toLowerCase().includes(q) || m.email.toLowerCase().includes(q));
  }, [orgMembers, memberUserIds, search]);

  async function handleAdd(userId: number) {
    if (isAnyLoading) return;
    setAddingId(userId);
    try {
      await addMember({ projectId, userId }).unwrap();
      setPrompt({
        title: t("members.addSuccessTitle"),
        text: t("members.addSuccessText"),
        variant: "success",
      });
    } catch (err: unknown) {
      setPrompt({
        title: t("members.errorTitle"),
        text: extractErrorMessage(err, "members.addErrorText", t),
        variant: "error",
      });
    } finally {
      setAddingId(null);
    }
  }

  async function handleRemove(userId: number) {
    if (isAnyLoading) return;
    setRemovingId(userId);
    try {
      await removeMember({ projectId, userId }).unwrap();
      setPrompt({
        title: t("members.removeSuccessTitle"),
        text: t("members.removeSuccessText"),
        variant: "success",
      });
    } catch (err: unknown) {
      setPrompt({
        title: t("members.errorTitle"),
        text: extractErrorMessage(err, "members.removeErrorText", t),
        variant: "error",
      });
    } finally {
      setRemovingId(null);
    }
  }

  return (
    <>
      <ModalWrapper onClose={onClose} width="max-w-6xl w-full">
        <h4 className="text-display-2xs content-base-primary">{t("members.title")}</h4>

        <div className="grid grid-cols-2 gap-6 min-h-0">
          <div className="flex flex-col min-h-0">
            <p className="text-label-sm content-base-secondary mb-3">{t("members.availableMembers")}</p>
            <div className="mb-6">
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t("members.search")}
                icon={<SearchNormal1 size={16} color="currentColor" />}
              />
            </div>
            <ScrollArea className="min-h-0 flex-1">
              <div className="flex flex-col gap-2 pr-2">
                {availableMembers.length === 0 ? (
                  <p className="text-body-regular-sm content-base-low py-4 text-center">{t("members.empty")}</p>
                ) : (
                  availableMembers.map((member) => (
                    <div key={member.id} className="flex items-center justify-between gap-3 pr-1">
                      <div className="flex items-center gap-3 min-w-0">
                        <Avatar name={member.name} textClassName="text-label-xs" />
                        <div className="flex flex-col min-w-0">
                          <span className="text-body-bold-sm content-base-primary truncate">{member.name}</span>
                          <span className="text-body-regular-xs content-base-secondary truncate">{member.email}</span>
                        </div>
                      </div>
                      <Button
                        isIconButton
                        variant="secondary"
                        className="w-7! h-7! p-0! rounded-lg! flex items-center justify-center content-action-brand shrink-0"
                        disabled={isAnyLoading}
                        onClick={() => handleAdd(member.id)}>
                        {addingId === member.id ? (
                          <span className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <Add size={16} color="currentColor" />
                        )}
                      </Button>
                    </div>
                  ))
                )}
              </div>
            </ScrollArea>
          </div>

          <div className="flex flex-col min-h-0 border-l surface-base-stroke pl-6">
            <p className="text-label-sm content-base-secondary mb-3">{t("members.currentMembers")}</p>
            <ScrollArea className="min-h-0 flex-1">
              <div className="flex flex-col gap-2 pr-2">
                {members.length === 0 ? (
                  <p className="text-body-regular-sm content-base-low py-4 text-center">{t("members.empty")}</p>
                ) : (
                  members.map((member) => (
                    <div key={member.id} className="flex items-center justify-between gap-3 pr-1">
                      <div className="flex items-center gap-3 min-w-0">
                        <Avatar name={member.name} textClassName="text-label-xs" />
                        <div className="flex flex-col min-w-0">
                          <span className="text-body-bold-sm content-base-primary truncate">{member.name}</span>
                          <span className="text-body-regular-xs content-base-secondary truncate">{member.email}</span>
                        </div>
                      </div>
                      <Button
                        type="button"
                        isIconButton
                        variant="secondary"
                        className="w-7! h-7! p-0! rounded-lg! flex items-center justify-center content-action-brand shrink-0"
                        disabled={isAnyLoading}
                        onClick={() => handleRemove(member.id)}>
                        {removingId === member.id ? (
                          <span className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <Minus size={16} color="currentColor" />
                        )}
                      </Button>
                    </div>
                  ))
                )}
              </div>
            </ScrollArea>
          </div>
        </div>
      </ModalWrapper>

      {prompt && (
        <Prompt
          onClose={() => setPrompt(null)}
          title={prompt.title}
          text={prompt.text}
          variant={prompt.variant}
          namespace="Common"
        />
      )}
    </>
  );
}
